import { Button, InlineStack } from "@medentic/polaris-react";
import { PlusCircleIcon } from "@medentic/polaris-icons";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Payment, AssignedTicket } from "@features/events/types";

interface PaymentAttendeesCellProps {
  payment: Payment;
  onAssignClick: (payment: Payment) => void;
}

// Helper functions
const getMissingAttendeeCount = (payment: Payment) =>
  payment.assignedTickets?.filter((t) => !t.taken).length ?? 0;

const hasMissingAttendees = (payment: Payment) =>
  getMissingAttendeeCount(payment) > 0;

const getAssignedAttendees = (payment: Payment): AssignedTicket[] =>
  payment.assignedTickets?.filter((t) => t.taken) ?? [];

export default function PaymentAttendeesCell({
  payment,
  onAssignClick,
}: PaymentAttendeesCellProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();

  const assignedAttendees = getAssignedAttendees(payment);
  const missingCount = getMissingAttendeeCount(payment);
  const hasMissing = hasMissingAttendees(payment);

  const handleViewAttendees = () => {
    const assignedIds = assignedAttendees
      .map((a) => a.attendeeId)
      .filter(Boolean);
    const params = new URLSearchParams();
    params.set("Ids", assignedIds.join(","));
    navigate(`/e/${hcc}/attendees?${params}`);
  };

  return (
    <InlineStack gap="200" align="end">
      {/* Show assigned attendee count (if any) */}
      {assignedAttendees.length > 0 && (
        <Button variant="plain" onClick={handleViewAttendees}>
          {String(assignedAttendees.length)}
        </Button>
      )}
      {/* Show assign button if there are missing attendees */}
      {hasMissing && (
        <Button
          icon={PlusCircleIcon}
          variant="tertiary"
          accessibilityLabel={t("eventTransactions.assignTickets")}
          onClick={() => onAssignClick(payment)}
        >
          {`+${missingCount}`}
        </Button>
      )}
      {/* Show dash if no tickets at all */}
      {(!payment.assignedTickets || payment.assignedTickets.length === 0) &&
        "-"}
    </InlineStack>
  );
}
